import { useQueries } from '@tanstack/react-query'
import { getEventosConteoPorEstado } from '../api/eventos'

/**
 * Hook optimizado para obtener los conteos de eventos por cada centro de trabajo
 * Filtra por el estado activo para que la suma coincida con el botón
 * Usa el endpoint /api/radar/eventos/conteo-por-estado que es mucho más eficiente
 *
 * @param {Array} centros - Array de centros de trabajo
 * @param {number} estadoEvento - Estado por el que filtrar
 * @returns {Object} - { conteosPorCentro: { [idCentro]: cantidad }, isLoading, hasError }
 */
export const useEventosConteoPorCentro = (centros, estadoEvento) => {
  // Asegurar que siempre tengamos un array (puede ser vacío)
  const centrosArray = centros || []

  // Crear queries para cada centro de trabajo
  // IMPORTANTE: Siempre llamar a useQueries, incluso con array vacío
  // NOTA: La API devuelve IdCentroTrabajo y Nombre con mayúsculas
  const queries = useQueries({
    queries: centrosArray.map((centro) => ({
      queryKey: ['eventos-conteo-centro', centro.IdCentroTrabajo, estadoEvento],
      queryFn: async () => {
        const filters = {
          idCentroTrabajo: centro.IdCentroTrabajo,
          estadoEvento, // Filtrar por el estado activo
        }

        const data = await getEventosConteoPorEstado(filters)

        // Sumar todos los conteos de los estados devueltos
        // (aunque con el filtro estadoEvento solo habrá uno con cantidad)
        let totalCount = 0
        if (data?.payload?.ConteoPorEstado) {
          data.payload.ConteoPorEstado.forEach((item) => {
            totalCount += item.Cantidad
          })
        }

        return {
          idCentroTrabajo: centro.IdCentroTrabajo,
          count: totalCount,
        }
      },
      staleTime: 1000 * 30, // 30 segundos
      refetchInterval: 1000 * 60, // Refrescar cada minuto
    })),
  })

  // Transformar resultados en un objeto { idCentroTrabajo: count }
  const conteosPorCentro = {}
  let isLoading = false
  let hasError = false

  queries.forEach((query) => {
    if (query.isLoading) isLoading = true
    if (query.isError) hasError = true

    if (query.data) {
      conteosPorCentro[query.data.idCentroTrabajo] = query.data.count
    }
  })

  return {
    conteosPorCentro,
    isLoading,
    hasError,
  }
}
